import { makeRequest } from "../../lib/api/request";
import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";

const PaySimple = () => {
  const [params] = useSearchParams();
  const userId = params.get("userID");
  const dataId = params.get("dataID");
  const sessionToken = params.get("session_token");

  const [customerToken, setCustomerToken] = useState("");
  const [signature, setSignature] = useState("");
  const [formValid, setFormValid] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const paysimpleJsRef = useRef(null);
  const containerRef = useRef(null);
  const initializedRef = useRef(false);
  const signatureRef = useRef("");

  const loadCustomerToken = useCallback(() => {
    if (!userId || !dataId) return;
    setLoading(true);
    setError(null);
    const headers = sessionToken ? { "X-Card-Session-Token": sessionToken } : {};
    makeRequest("/paysimple/checkout_token/", "POST", {
      user_id: userId,
      data_id: dataId,
    }, "", headers)
      .then((r) => {
        if (!r.CustomerToken) {
          setError("Failed to load payment form. Please refresh the page.");
          return;
        }
        if (!r.signature) {
          setError("Failed to load payment form. Please refresh the page.");
          return;
        }
        setCustomerToken(r.CustomerToken);
        setSignature(r.signature);
        signatureRef.current = r.signature || "";
      })
      .catch((err) => {
        console.error("PaySimple customer token error:", err);
        setError("Failed to load payment form. Please refresh the page.");
      })
      .finally(() => setLoading(false));
  }, [userId, dataId]);

  const initializePaySimpleJs = useCallback(() => {
    if (!customerToken || !window.paysimpleJs || !containerRef.current || initializedRef.current) return;
    
    try {
      if (containerRef.current.children.length > 0) {
        containerRef.current.innerHTML = "";
      }

      initializedRef.current = true;
      const psjs = window.paysimpleJs({
        container: containerRef.current,
        auth: { token: customerToken },
        bypassPostalCodeValidation: false,
        preventAutocomplete: false,
        styles: {
          body: {
            backgroundColor: "#f9f9f9",
          },
          container: {
            marginBottom: "20px",
          },
          field: {
            height: "50px",
            fontSize: "16px",
            padding: "8px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxSizing: "border-box",
            focus: {
              borderColor: "#007bff",
              outline: "none",
            },
          },
          label: {
            display: "block",
            marginBottom: "8px",
            fontWeight: "500",
          },
        },
        text: {
          labels: {
            cardNumber: "Credit Card Number",
            expiration: "Expiration (mm/yy)",
            cvv: "CVV",
            postalCode: "Zip/Postal Code",
          },
          placeholder: {
            cardNumber: "Credit Card Number",
            expiration: "Expiration (mm/yy)",
            cvv: "CVV",
            postalCode: "Zip Code",
          },
        },
      });
      psjs.send.setMode("cc-key-enter");
      psjs.on("formValidityChanged", function (body) {
        setFormValid(body.validity === "true" || body.validity === true);
      });
      psjs.on("httpError", function (err) {
        console.error("PaySimpleJS httpError:", err);
        const errorMsg = err?.errors?.[0]?.message || err?.errorKey || "Payment form error.";
        setError(errorMsg);
        setLoading(false);
      });
      psjs.on("accountRetrieved", function (accountInfo) {
        const currentSignature = signatureRef.current;
        if (!currentSignature) {
          setError("Payment form error. Please refresh the page.");
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);
        makeRequest("/paysimple/payment_method/", "POST", {
          signature: currentSignature,
          account_id: accountInfo.account?.id,
          customer_id: accountInfo.customer?.id,
          first_name: accountInfo.customer?.firstName || "",
          last_name: accountInfo.customer?.lastName || "",
        })
          .then(() => {
            setSuccess(true);
            window.parent.postMessage("PAYMENT_SUCCESS", "*");
          })
          .catch((err) => {
            const msg = err.message || "Failed to save payment method.";
            setError(msg);
            if (err.response && err.response.status >= 400) {
              window.parent.postMessage("PAYMENT_FAIL", "*");
            }
          })
          .finally(() => setLoading(false));
      });
      paysimpleJsRef.current = psjs;
      setFormReady(true);
    } catch (err) {
      console.error("PaySimpleJS init error:", err);
      initializedRef.current = false;
      setError("Failed to initialize payment form. Please refresh the page.");
    }
  }, [customerToken, userId, dataId]);


  useEffect(() => {
    if (userId && dataId) loadCustomerToken();
  }, [userId, dataId, loadCustomerToken]);

  useEffect(() => {
    if (customerToken && window.paysimpleJs && !initializedRef.current) {
      initializePaySimpleJs();
    } else if (customerToken && !window.paysimpleJs && !initializedRef.current) {
      const checkScript = setInterval(() => {
        if (window.paysimpleJs && !initializedRef.current) {
          clearInterval(checkScript);
          initializePaySimpleJs();
        }
      }, 100);

      const timeout = setTimeout(() => {
        clearInterval(checkScript);
        if (!window.paysimpleJs) {
          setError("Payment library failed to load. Please refresh the page.");
        }
      }, 5000);

      return () => {
        clearInterval(checkScript);
        clearTimeout(timeout);
      };
    }
  }, [customerToken, initializePaySimpleJs]);

  const formContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "500px",
    margin: "0 auto",
    padding: "20px",
  };

  return (
    <>
      {userId && dataId && (
        <>
          {!success ? (
            <div style={formContainerStyle}>
              <div
                ref={containerRef}
                style={{
                  marginBottom: "20px",
                  minHeight: "100px",
                  width: "100%",
                  maxWidth: "460px",
                }}
              />
              {error && (
                <div style={{ color: "red", textAlign: "center", marginBottom: "10px", width: "100%" }}>
                  {error}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "center", marginTop: "20px", width: "100%" }}>
                <Button
                  type="button"
                  disabled={loading || !formReady || !formValid}
                  onClick={() => {
                    if (!paysimpleJsRef.current || !paysimpleJsRef.current.send) return;

                    setLoading(true);
                    setError(null);

                    try {
                      paysimpleJsRef.current.send.matchOrCreateAccount({ token: customerToken });
                    } catch (err) {
                      console.error("PaySimple account error:", err);
                      setError("Failed to process payment. Please try again.");
                      setLoading(false);
                    }
                  }}
                  style={{ minWidth: "120px" }}
                >
                  {loading ? "Processing..." : "Submit"}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <h2>Payment method successfully added.</h2>
            </div>
          )}
        </>
      )}
      {(!userId || !dataId) && (
        <div style={{ textAlign: "center" }}>
          <h2>Missing user or data ID.</h2>
        </div>
      )}
    </>
  );
};

export default PaySimple;
